import React, { useState } from 'react';
import { Modal, message } from 'antd';
import { theme } from 'utils/style/theme';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import iconClose from 'utils/icon/icon_close_circle.svg';
import { handleMessage } from 'utils/helper/message';
import PrimaryButton from 'components/button/PrimaryButton';
import InputStyled from 'components/InputStyled';
import ColorPicker from '../_component/ColorPicker';
import { nameTagApi } from 'apis/resource/nameTagApis';

/**
 * * 리소스 - 미니컷 등록 모달
 */

interface IProps {
  visible: {
    create: boolean;
    edit: boolean;
    delete: boolean;
    name: string;
  };
  setVisible: (e: any) => void;
  setRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  refresh: boolean;
  projectId: string | undefined;
}

const NameTagCreateModal = ({
  visible,
  setVisible,
  setRefresh,
  refresh,
  projectId,
}: IProps) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState({
    speaker: '',
    main_color: '#000000',
    sub_color: '#000000',
    KO: '',
    EN: '',
    JA: '',
    AR: '',
    ID: '',
    ES: '',
    MS: '',
    RU: '',
    ZH: '',
    SC: '',
  });

  const [loading, setLoading] = useState(false);

  /// 서버 전송
  const handleSubmit = async () => {
    if (projectId) {
      try {
        setLoading(true);
        await nameTagApi.nameTagCreate(projectId, inputValue);
        setRefresh(!refresh);
        setLoading(false);
        handleClose();
      } catch (err) {
        setLoading(false);
        handleMessage(message, 'error', 'Upload failed');
      }
    }
  };

  /// 모달 닫기
  const handleClose = () => {
    setVisible({ ...visible, create: false });
    setInputValue({
      speaker: '',
      main_color: '#000000',
      sub_color: '#000000',
      KO: '',
      EN: '',
      JA: '',
      AR: '',
      ID: '',
      ES: '',
      MS: '',
      RU: '',
      ZH: '',
      SC: '',
    });
    // 폼을 비워줘야 이미지 첨부한 것도 비워진다.
  };

  const handleInputChange = (value, type) => {
    const cloneData = JSON.parse(JSON.stringify(inputValue));
    cloneData[type] = value;
    setInputValue(cloneData);
  };

  return (
    <CustomModal
      title={t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.nameTagContent.createModal.title',
      )}
      visible={visible.create}
      onCancel={handleClose}
      footer={
        <ButtonWrapper>
          <PrimaryButton
            text={t(
              'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.emoticonContent.createGroupModal.button',
            )}
            height='40px'
            width='fit-content'
            bgColor={theme.colors.blue_500}
            textColor='#ffffff'
            fontWeight={theme.fontWeights.medium}
            fontSize={theme.fontSizes.normal}
            border={`1px solid ${theme.colors.blue_500}`}
            rounded='6px'
            hoverBgColor={theme.colors.blue_600}
            activeBgColor={theme.colors.blue_800}
            selectedBgColor={theme.colors.blue_700}
            onClick={handleSubmit}
            loading={loading}
            disabled={loading}
          />
        </ButtonWrapper>
      }
      closeIcon={
        <img src={iconClose} width={20} height={20} alt='icon-close' />
      }
    >
      <SectionWarpper>
        <div>
          <p className='title'>Speaker</p>
          <InputStyled
            value={inputValue.speaker}
            onChange={(e) => {
              handleInputChange(e.target.value, 'speaker');
            }}
            maxLength={30}
          />
          <p className='length-subtext'>
            {typeof inputValue.speaker.length === 'number'
              ? `${inputValue.speaker.length}/30 characters`
              : '0/30 characters'}
          </p>
        </div>
        <div className='colorsWarpper'>
          <div>
            <p className='title'>Main color:</p>
            <ColorPicker
              inputValue={inputValue}
              setInputValue={setInputValue}
              type='main_color'
            />
          </div>
          <div>
            <p className='title'>Sub color:</p>
            <ColorPicker
              inputValue={inputValue}
              setInputValue={setInputValue}
              type='sub_color'
            />
          </div>
        </div>
        <div className='languagesWrapper'>
          <div>
            <p className='title'>Korean</p>
            <InputStyled
              value={inputValue.KO}
              onChange={(e) => {
                handleInputChange(e.target.value, 'KO');
              }}
              maxLength={30}
            />
            <p className='length-subtext'>
              {typeof inputValue.KO.length === 'number'
                ? `${inputValue.KO.length}/30 characters`
                : '0/30 characters'}
            </p>
          </div>
          <div>
            <p className='title'>Indonesia</p>
            <InputStyled
              value={inputValue.ID}
              onChange={(e) => {
                handleInputChange(e.target.value, 'ID');
              }}
              maxLength={30}
            />
            <p className='length-subtext'>
              {typeof inputValue.ID.length === 'number'
                ? `${inputValue.ID.length}/30 characters`
                : '0/30 characters'}
            </p>
          </div>
          <div>
            <p className='title'>English</p>
            <InputStyled
              value={inputValue.EN}
              onChange={(e) => {
                handleInputChange(e.target.value, 'EN');
              }}
              maxLength={30}
            />
            <p className='length-subtext'>
              {typeof inputValue.EN.length === 'number'
                ? `${inputValue.EN.length}/30 characters`
                : '0/30 characters'}
            </p>
          </div>
          <div>
            <p className='title'>Español</p>
            <InputStyled
              value={inputValue.ES}
              onChange={(e) => {
                handleInputChange(e.target.value, 'ES');
              }}
              maxLength={30}
            />
            <p className='length-subtext'>
              {typeof inputValue.ES.length === 'number'
                ? `${inputValue.ES.length}/30 characters`
                : '0/30 characters'}
            </p>
          </div>
          <div>
            <p className='title'>Japanese</p>
            <InputStyled
              value={inputValue.JA}
              onChange={(e) => {
                handleInputChange(e.target.value, 'JA');
              }}
              maxLength={30}
            />
            <p className='length-subtext'>
              {typeof inputValue.JA.length === 'number'
                ? `${inputValue.JA.length}/30 characters`
                : '0/30 characters'}
            </p>
          </div>
          <div>
            <p className='title'>Malay</p>
            <InputStyled
              value={inputValue.MS}
              onChange={(e) => {
                handleInputChange(e.target.value, 'MS');
              }}
              maxLength={30}
            />
            <p className='length-subtext'>
              {typeof inputValue.MS.length === 'number'
                ? `${inputValue.MS.length}/30 characters`
                : '0/30 characters'}
            </p>
          </div>
          <div>
            <p className='title'>Arabic</p>
            <InputStyled
              value={inputValue.AR}
              onChange={(e) => {
                handleInputChange(e.target.value, 'AR');
              }}
              maxLength={30}
            />
            <p className='length-subtext'>
              {typeof inputValue.AR.length === 'number'
                ? `${inputValue.AR.length}/30 characters`
                : '0/30 characters'}
            </p>
          </div>
          <div>
            <p className='title'>Russian</p>
            <InputStyled
              value={inputValue.RU}
              onChange={(e) => {
                handleInputChange(e.target.value, 'RU');
              }}
              maxLength={30}
            />
            <p className='length-subtext'>
              {typeof inputValue.RU.length === 'number'
                ? `${inputValue.RU.length}/30 characters`
                : '0/30 characters'}
            </p>
          </div>
          <div>
            <p className='title'>Taiwan</p>
            <InputStyled
              value={inputValue.ZH}
              onChange={(e) => {
                handleInputChange(e.target.value, 'ZH');
              }}
              maxLength={30}
            />
            <p className='length-subtext'>
              {typeof inputValue.ZH.length === 'number'
                ? `${inputValue.ZH.length}/30 characters`
                : '0/30 characters'}
            </p>
          </div>
          <div>
            <p className='title'>Italy</p>
            <InputStyled
              value={inputValue.SC}
              onChange={(e) => {
                handleInputChange(e.target.value, 'SC');
              }}
              maxLength={30}
            />
            <p className='length-subtext'>
              {typeof inputValue.SC.length === 'number'
                ? `${inputValue.SC.length}/30 characters`
                : '0/30 characters'}
            </p>
          </div>
        </div>
      </SectionWarpper>
    </CustomModal>
  );
};

export default NameTagCreateModal;

const CustomModal = styled(Modal)`
  .ant-modal-body {
    padding: 0;
  }
  .ant-modal-content {
    box-shadow: none;
    width: 566px;
  }
  .ant-radio-wrapper {
    span {
      font-size: ${theme.fontSizes.normal};
      font-weight: 400;
      line-height: 24px;
      letter-spacing: 0.2px;
      color: ${theme.colors.dark_500};
    }
  }
  .ant-modal-footer {
    margin-top: 10px !important;
  }
  ul {
    margin-left: 25px;
  }
  .ant-modal-close:hover {
    background-color: transparent;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: end;
`;

const SectionWarpper = styled.div`
  & > * + * {
    margin-top: 8px;
  }
  .colorsWarpper,
  .languagesWrapper {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }
`;
